import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from "moment";
import { IoIosArrowDown } from "react-icons/io";
import { useDispatch, useSelector } from 'react-redux';
import { fetchBetHistoryRoundData } from './cl_BetHistory.Slice';
import PaginationComponent from '../common/PaginationComponent';

const NewGameHistory = ({ currentPage, setCurrentPage }) => {

    const [openIndices, setOpenIndices] = useState([0]);
    const [loadingRounds, setLoadingRounds] = useState({});
    let PageSize = 10;

    const dispatch = useDispatch();
    const firstRoundFetched = useRef(false);

    const { casinoHistory, casinoTotalRecords, casinoRecords, casinoRoundData } = useSelector(
        (state) => state?.betHistory
    );

    const bethistRecords = casinoTotalRecords + casinoRecords;

    const formatMoneyValues = (num) => {
        return Number(num || 0).toFixed(2);
    };

    const sortedCasinoBetHistoryData = useMemo(() => {
        if (casinoHistory?.length > 0) {
            return [...casinoHistory].sort(
                (a, b) => new Date(b.placedDate) - new Date(a.placedDate)
            );
        }
        return [];
    }, [casinoHistory]);

    const toggleAccordion = async (index, roundId) => {
        setOpenIndices((prev) => {
            if (prev.includes(index)) {
                return prev.filter((i) => i !== index);
            } else {
                return [...prev, index];
            }
        });

        if (!casinoRoundData[roundId]) {
            setLoadingRounds(prev => ({ ...prev, [roundId]: true }));
            await dispatch(fetchBetHistoryRoundData(roundId));
            setLoadingRounds(prev => ({ ...prev, [roundId]: false }));
        }
    };

    const betStatusCheck = (bet) => {
        const status = bet?.betStatus?.toLowerCase();
        if (status === "running") return "Pending";
        if (status === "cancelled") return "NA";
        return formatMoneyValues(bet?.payout) ?? "-";
    };

    const getAmount = (item) => {
        if (item.stake > 0 && item.betTransactionType !== "WIN_REQUEST") return formatMoneyValues(item.stake);
        if (Number(item.payout) > 0 && item.betTransactionType === "WIN_REQUEST") return formatMoneyValues(item.payout);
        return 0;
    };

    const getBetStatus = (round) => {
        if (round.betTransactionType === "BET_REQUEST") {
            if (!round.placeBetType) return round.placeBetType;
            return round.placeBetType
                .toLowerCase()
                .split(/[\s_]+/)
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        } else if (round.betTransactionType === "WIN_REQUEST") {
            return round.betStatus !== "Cancelled" ? "Win" : "Cancel";
        } else return "-"
    };

    useEffect(() => {
        if (!firstRoundFetched.current && sortedCasinoBetHistoryData?.length > 0) {
            const firstRoundId = sortedCasinoBetHistoryData[0]?.roundId;
            if (firstRoundId) {
                firstRoundFetched.current = true;
                setOpenIndices([0]);
                setLoadingRounds(prev => ({ ...prev, [firstRoundId]: true }));
                dispatch(fetchBetHistoryRoundData(firstRoundId)).finally(() => {
                    setLoadingRounds(prev => ({ ...prev, [firstRoundId]: false }));
                });
            }
        }
    }, [sortedCasinoBetHistoryData, dispatch]);


    if (sortedCasinoBetHistoryData.length <= 0) {
        return (
            <div className="SB-betHistory-liveCasino">
                <div className="SB-myBetsBox-group">
                    <div className="SB-msgBox">
                        <div className="SB-msgBox-container">
                            Currently there is no data available
                        </div>
                    </div>
                </div>
            </div >
        )
    }

    return (
        <div className="SB-tab-content">
            {sortedCasinoBetHistoryData?.map((each, index) => {
                const isOpen = openIndices.includes(index);
                const roundData = casinoRoundData[each.roundId] || [];
                const isLoading = loadingRounds[each.roundId];

                return (
                    <div key={each.roundId}>
                        <div className="SB-myBetsBox-group">
                            <div className="SB-betBox SB-gameBet SB-accordion" style={{borderRadius: "10px" }}>
                                <div
                                    className="SB-betBox-header SB-accordion-header"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => toggleAccordion(index, each.roundId)}
                                >
                                    <div className="SB-contentBlock" style={{ paddingLeft: "0px" }}>
                                        <div className="SB-leftContent" style={{ display: "flex", alignItems: "center" }}>
                                            <IoIosArrowDown
                                                fontSize="16px"
                                                style={{
                                                    marginRight: "4px",
                                                    transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                                                    transition: "transform 0.3s ease"
                                                }}
                                            />
                                            <span className="SB-betType">{each.gameName}</span>
                                        </div>
                                        <div className="SB-rightContent">
                                            <span
                                                className={`SB-badge ${each?.betStatus.toLowerCase() === "lost"
                                                    ? "SB-lost"
                                                    : each?.betStatus.toLowerCase() === "won"
                                                        ? "SB-won"
                                                        : ""
                                                    }`}
                                            >
                                                {each?.betStatus}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="SB-betBox-betStatusDetails">
                                    <div className="SB-contentBlock">
                                        <div className="SB-leftContent">
                                            <div className="SB-betID">Round ID: {each?.roundId}</div>
                                        </div>
                                    </div>
                                    <div className="SB-placedOnDate">
                                        Placed on:{" "}
                                        {`${moment(each?.placedDate).format("DD MMM")} at ${moment(each?.placedDate).format("HH:mm")}`}
                                    </div>
                                </div>

                                {isOpen && (
                                    <div className="SB-accordion-content" style={{ paddingLeft: "10px", paddingRight: "10px" }}>
                                        {isLoading ? (
                                            <div className="SB-section-loader">
                                                <div className="SB-section-loader-container">
                                                    <span className="SB-dot"></span>
                                                    <span className="SB-dot"></span>
                                                    <span className="SB-dot"></span>
                                                    <span className="SB-dot"></span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="SB-userLevel-table SB-table-scrollable">
                                                {roundData.length > 0 ? (
                                                    <table>
                                                        <thead>
                                                            <tr>
                                                                <th>Txn. ID</th>
                                                                <th>Amount</th>
                                                                <th>Type</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {roundData?.map((round) => {
                                                                if (round.payout <= 0 && round.betTransactionType === "WIN_REQUEST") return null;
                                                                if (round.stake <= 0 && round.betTransactionType === "BET_REQUEST") return null;
                                                                return (
                                                                    <tr key={round.betId}>
                                                                        <td style={{ textAlign: "center" }}>{round.betId}</td>
                                                                        <td>{round.currencyCode?.toUpperCase()} {getAmount(round)}</td>
                                                                        <td><span>{getBetStatus(round)}</span></td>
                                                                    </tr>
                                                                )
                                                            })}
                                                        </tbody>
                                                    </table>
                                                ) : (
                                                    <div className="SB-msgBox">
                                                        <div className="SB-msgBox-container">
                                                            Currently there is no data available
                                                        </div>
                                                    </div>
                                                )}

                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="SB-betBox-stakeRetuns">
                                    <div className="SB-contentBlock SB-betBox-stake">
                                        <div>
                                            Stake: <b>{each.currencyCode} {formatMoneyValues(each.stake)}</b>
                                        </div>
                                        <div className="SB-rightContent">
                                            <div>
                                                To Payout: <b>{betStatusCheck(each)}</b>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            })}
            {sortedCasinoBetHistoryData?.length > 0 && (
                <PaginationComponent
                    currentPage={currentPage}
                    totalCount={bethistRecords || 0}
                    pageSize={PageSize}
                    onPageChange={(page) => setCurrentPage(page)}
                    setCurrentPage={setCurrentPage}
                />
            )}
        </div>
    )
}

export default NewGameHistory;
